import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import type { DocumentTemplate } from 'generated/api';

/**
 * Resolves a document template's fax number from its id.
 *
 * The cached `state.templates.templates` list comes from GET /templates, which
 * returns DocumentTemplateLite — id, description, category, detection status,
 * and nothing else. `fax` lives only on the full DocumentTemplate, so a lookup
 * against the cache silently misses for every template the user has not opened
 * in the editor this session. We fetch the full template by id instead; that
 * fulfilment upserts it into the same list, so a second lookup is free.
 *
 * `loading` matters: callers preselecting a fax number must wait for it to
 * clear before deciding there is none, otherwise they lock in an empty field
 * one render before the real number arrives.
 */
export function useTemplateFax(templateId?: string | null): {
  fax: string | undefined;
  loading: boolean;
} {
  const dispatch = useAppDispatch();
  const cachedFax = useAppSelector((state) =>
    templateId ? state.templates.templates.find((t) => t.id === templateId)?.fax : undefined,
  );

  // Tagged with the id it was resolved for, so switching templates never
  // exposes the previous template's fax while the new lookup is in flight.
  const [resolved, setResolved] = useState<{ id: string; fax?: string } | null>(null);
  const requestedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!templateId || cachedFax) return undefined;
    if (requestedRef.current === templateId) return undefined;
    requestedRef.current = templateId;
    let cancelled = false;
    // No errorTitle: a deleted/inaccessible template resolves to "no fax"
    // rather than a toast over the send modal. Rejections resolve too, so a
    // failed lookup can't wedge `loading` forever.
    dispatch(api.Templates.getDocumentTemplate({ id: templateId }))
      .then((template?: DocumentTemplate) => {
        if (!cancelled) setResolved({ id: templateId, fax: template?.fax });
      })
      .catch(() => {
        if (!cancelled) setResolved({ id: templateId, fax: undefined });
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, cachedFax, dispatch]);

  if (!templateId) return { fax: undefined, loading: false };
  if (cachedFax) return { fax: cachedFax, loading: false };

  const current = resolved && resolved.id === templateId ? resolved : null;
  return { fax: current?.fax, loading: !current };
}
